import { Location } from '@angular/common';
import { Component, DoCheck, Input, KeyValueDiffer, KeyValueDiffers, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { EnvironmentVariablesService } from '@kenv';
import { LocalResourcesBloc } from '@kp/local-resources/local-resources.bloc';
import { NotificationsBloc } from '@kp/notifications/notifications.bloc';
import { SearchBloc } from '@kp/search/search.bloc';
import { CardCollectionService } from '@kp/shared/components/cards/card-collection.service';
import { CardBloc, DetailCardData } from '@kp/shared/components/cards/card.bloc';
import { ThemeService } from '@kservice';
import {
  CardDisplayType,
  CardEventType,
  CardItemState,
  CardRequestType,
  CardScreen,
  CustomCardCategory,
  Product,
} from '@ktypes/enums';
import { CardContentType, CardEvent, CardItem, UserCardState } from '@ktypes/models';
import { DateTimeUtil } from '@kutil';
import { DayOfTheWeekRange } from 'cron-parser/types';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'kp-card',
  templateUrl: './card.component.html',
  styleUrls: ['./card.component.scss'],
})
export class CardComponent implements OnInit, OnDestroy, DoCheck {
  @Input() allCompleted = false;
  @Input() cardId: string;
  @Input() customCardCategory: CustomCardCategory;
  @Input() cardDisplayType: CardDisplayType;
  @Input() noReminders = false;
  @Input() screen: CardScreen;
  @Input() requestType: CardRequestType;

  private _destroy$ = new Subject<void>();
  private _userStateDiffer: KeyValueDiffer<string, any>;
  private _saveStateDiffer: KeyValueDiffer<string, any>;

  card: CardItem;
  cardScreen = CardScreen;
  detailViewClicked: boolean;
  clickEnabled: boolean;
  cardCategory = CustomCardCategory;
  cardContentType = CardContentType;
  cardDisplayTypes = CardDisplayType;
  cardRequestType = CardRequestType;
  cardUrl: string;
  isPurposeful = true;
  reminderScheduleText: string;
  shouldShowAnimation = false;
  shouldShowCard = false;
  tertiaryCardInfo: string;

  constructor(
    private _activatedRoute: ActivatedRoute,
    public cardBloc: CardBloc,
    private _cardCollectionService: CardCollectionService,
    private _differs: KeyValueDiffers,
    private _environmentVariablesService: EnvironmentVariablesService,
    private _localResourcesBloc: LocalResourcesBloc,
    private _notificationsBloc: NotificationsBloc,
    private _location: Location,
    private _searchBloc: SearchBloc,
    public themeService: ThemeService
  ) {}

  // Changing to doCheck due to changes in card userState not being detected by component
  ngDoCheck() {
    this.card = this._cardCollectionService.cardsById[this.cardId];
    const userStateChanges = this._userStateDiffer?.diff(this.card?.userState);
    const saveStateChanges = this._saveStateDiffer?.diff(this.card?.userState?.saveState);
    if (userStateChanges || saveStateChanges) {
      this._update();
    }
  }

  ngOnInit() {
    this.card = this._cardCollectionService.cardsById[this.cardId];
    this._userStateDiffer = this._differs.find(this.card?.userState ?? {}).create();
    this._saveStateDiffer = this._differs.find(this.card?.userState?.saveState ?? {}).create();
    this.cardBloc.clickEnabled.next(true);
    this.isPurposeful = this._environmentVariablesService.product === Product.purposeful;
    this.cardBloc.detailViewClicked.pipe(takeUntil(this._destroy$)).subscribe((data: DetailCardData) => {
      if (data) {
        this.detailViewClicked = data.detailViewClicked;
      }
    });
    this.cardBloc.clickEnabled.pipe(takeUntil(this._destroy$)).subscribe((clickEnabled) => {
      this.clickEnabled = clickEnabled;
    });
  }

  ngOnDestroy() {
    this._destroy$.next();
    this._destroy$.complete();
  }

  handleKeyboardEvent(event: KeyboardEvent): void {
    if (event.key.toLowerCase() === 'enter') {
      this.clickEnabled = true;
      this.detailView(event);
    }
  }

  detailView(event) {
    event.preventDefault();
    if (this.card.contentType === CardContentType.localResource) {
      this._openLocalResourceDetailView();
    } else {
      this._openContentCardDetailView();
    }
  }

  likeChanged() {
    this.sendCardEvent(CardEventType.MORE);
  }

  sendCardEvent(eventType: CardEventType) {
    void this.cardBloc.handleCardEvents(
      new CardEvent(
        this.card.id,
        this.card,
        eventType,
        !this._searchBloc.isSearchState ? this.requestType : CardRequestType.SEARCH
      )
    );
  }

  private _update() {
    this._calculateCardUrl();
    this._calculateShouldShowAnimation();
    this._calculateShouldShowCard();
    this._calculateTertiaryCardInfo();
    this._calculateReminderSchedule();
  }

  private _openContentCardDetailView() {
    if (this.clickEnabled) {
      this.cardBloc.detailViewClicked.next({
        detailViewClicked: true,
        card: this.card,
        customCardCategory: this.customCardCategory,
        requestType: this.requestType,
        screen: this.screen,
      });
      let cardEventType = CardEventType.OPEN;
      let cardEventMeta;
      let isSearchScreen = false;

      if (this.requestType === CardRequestType.AVAILABLE || this.requestType === CardRequestType.RESOURCES) {
        isSearchScreen = true;
      }

      if (this._searchBloc.isSearchState && isSearchScreen) {
        cardEventType = CardEventType.SEARCH_OPEN;
        cardEventMeta = {
          search_id: this._searchBloc.lastSearchId,
          search_type: this._searchBloc.lastSearchType,
          search_page: this.screen,
        };
      }
      void this.cardBloc.handleCardEvents(
        new CardEvent(this.card.id, this.card, cardEventType, this.requestType, cardEventMeta)
      );

      if (this.screen === CardScreen.saved || this.screen === CardScreen.category) {
        this._location.go(`/cards/${this.customCardCategory}/${this.card.id}`);
      } else if (this.screen === CardScreen.challenges) {
        this._location.go(`/challenges/${this._activatedRoute.snapshot.paramMap.get('challengeId')}/${this.card.id}`);
      } else {
        this.screen === CardScreen.recommended || this.requestType === CardRequestType.TRACKING
          ? this._location.go(`/today/${this.card.id}`)
          : this._location.go(`/${this.screen}/${this.card.id}`);
      }
    }
  }

  private _openLocalResourceDetailView() {
    this._localResourcesBloc.handleCardEvents(
      new CardEvent(this.card.localResourceId, this.card, CardEventType.LOCAL_RESOURCE_OPENED, CardRequestType.SAVED)
    );
    this._localResourcesBloc.detailViewClicked.next({
      card: this.card,
      customCardCategory: this.customCardCategory,
      detailViewClicked: true,
      requestType: CardRequestType.SAVED,
      screen: this.screen,
      source: 'saved',
      zip: this.card.userState.saveState.postalCode,
    });
    if (this.screen === CardScreen.saved || this.screen === CardScreen.category) {
      this._location.go(`/cards/${this.customCardCategory}/${this.card.localResourceId}`);
    } else {
      this._location.go(`/${this.screen}/${this.card.localResourceId}`);
    }
  }

  private _calculateCardUrl() {
    if (this.screen === CardScreen.saved || this.screen === CardScreen.category) {
      this.cardUrl = `/cards/${this.customCardCategory}/${this.card.id}`;
    } else if (this.screen === CardScreen.challenges) {
      this.cardUrl = `/challenges/${this._activatedRoute.snapshot.paramMap.get('challengeId')}/${this.card.id}`;
    } else {
      this.cardUrl =
        this.screen === CardScreen.recommended || this.requestType === CardRequestType.TRACKING
          ? `/today/${this.card.id}`
          : `/${this.screen}/${this.card.id}`;
    }
  }

  private _calculateShouldShowAnimation() {
    if (this.card) {
      const _userState: UserCardState = this.card.userState;
      this.shouldShowAnimation =
        _userState.shownAnimation !== true &&
        _userState.currentState === CardItemState.ACT_ANIMATION &&
        this.requestType !== CardRequestType.TOTD;
    } else {
      this.shouldShowAnimation = false;
    }
  }

  private _calculateShouldShowCard() {
    this.shouldShowCard = !this.shouldShowAnimation;
  }

  private _calculateTertiaryCardInfo() {
    if (this.card.userState?.isCompleted) {
      this.tertiaryCardInfo = 'Completed!';
      return;
    }
    this.tertiaryCardInfo = this.card.topic;

    // Has at least 1 other thing to add
    if (
      this.card.userState.availableItemCount ||
      this.card.completionTime ||
      this.card.userState.isRecommended ||
      this.card.isUserSubmitted
    ) {
      // Has completion time, and is not tracked / not user submitted / not recommended
      if (
        this.card.completionTime &&
        (!this.card.userState?.isTracking || (!this.card.isUserSubmitted && !this.card.userState.isRecommended))
      ) {
        this.tertiaryCardInfo += ` • ${this.card.completionTime} min`;
        return;
      }

      // Is user submitted, and does not have completion time / is not recommended
      if (this.card.isUserSubmitted && !this.card.completionTime && !this.card.userState.isRecommended) {
        this.tertiaryCardInfo += ' • User Submitted';
        return;
      }

      // Is recommended, and is not completed, no completion time, no completed items, not user submitted
      if (
        this.card.userState.isRecommended &&
        !this.card.completionTime &&
        !this.card.isUserSubmitted &&
        !this.card.userState?.isCompleted &&
        this.card.userState.completedItemCount === 0
      ) {
        this.tertiaryCardInfo += ' • Recommended';
        return;
      }

      // Has completed items
      if (this.card.userState.completedItemCount > 0 && this.card.userState.availableItemCount) {
        this.tertiaryCardInfo += ` • ${this.card.userState.completedItemCount} of ${this.card.userState.availableItemCount} complete`;
        return;
      }

      // No completed items, has availableItemCount and not recommended
      if (
        this.card.userState.completedItemCount === 0 &&
        this.card.userState.availableItemCount &&
        !this.card.userState.isRecommended
      ) {
        this.tertiaryCardInfo += ` • ${this.card.userState.availableItemCount} steps`;
        return;
      }
    }
  }

  private _calculateReminderSchedule() {
    if (this.cardDisplayType === CardDisplayType.checklist) {
      this.reminderScheduleText = getTakeActionReminderSchedule(this.card);
    } else {
      if (this.card?.userState?.notifications?.length > 1) {
        this.reminderScheduleText = 'Multiple';
      } else {
        const notification = this.card?.userState?.notifications?.[0];
        if (notification) {
          const interval = notification.interval;
          const time = DateTimeUtil.getTimeFromCron(notification.notificationSchedule);
          const days = interval.fields.dayOfWeek.map((dayIndex) => DateTimeUtil.everyDay[dayIndex]);
          const selectedDays = this._notificationsBloc.getSelectedDays(days);
          this.reminderScheduleText = `${selectedDays} • ${time}`;
        }
      }
    }
  }
}

function getTakeActionReminderSchedule(card: CardItem): string {
  const currentDay = new Date().getDay();
  let reminderText: string;
  const notificationsForToday = card.userState?.notifications?.filter((notification) =>
    notification.interval.fields.dayOfWeek.includes(currentDay as DayOfTheWeekRange)
  );
  if (notificationsForToday?.length > 0) {
    if (notificationsForToday?.length > 1) {
      reminderText = 'Multiple reminders';
    } else {
      reminderText = DateTimeUtil.getTimeFromCron(notificationsForToday?.[0]?.notificationSchedule);
    }
  }

  if (card.userState?.habits?.length > 0 && card.userState.habits[0].value) {
    if (notificationsForToday?.length > 0) {
      reminderText += ` • ${card.userState.habits[0].value}`;
    } else {
      reminderText = card.userState.habits[0].value;
    }
  }

  if (card.isQuestCard) {
    let questText = 'Quest';

    if (card.userState.availableItemCount) {
      questText +=
        card.userState.completedItemCount > 0
          ? ` • ${card.userState.completedItemCount} of ${card.userState.availableItemCount} completed`
          : ` • ${card.userState.availableItemCount} steps`;
    }

    if (reminderText) {
      reminderText += ` • ${questText}`;
    } else {
      reminderText = `${questText}`;
    }
  }

  return reminderText;
}
